import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { ToastProvider } from './contexts/ToastContext';

// Layout Components
// #370 — top-level crash recovery; before this, any render error
// white-screened the entire app (see #353's shipped ReferenceErrors).
import ErrorBoundary from './components/ui/ErrorBoundary';
import Layout from './components/layout/Layout';
import AuthLayout from './components/layout/AuthLayout';
import PublicLayout from './layouts/PublicLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import OnboardingGuard from './components/onboarding/OnboardingGuard';

// Auth Components
import LoginForm from './components/auth/LoginForm';
import RegistrationForm from './components/auth/RegistrationForm';
import EmailVerification from './components/auth/EmailVerification';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import SessionTimeoutProvider from './components/auth/SessionTimeoutProvider';
import MFASetup from './components/auth/MFASetup';
import MFAVerification from './components/auth/MFAVerification';

// Public pages
const LandingPage = lazy(() => import('./pages/public/LandingPage'));
const Features = lazy(() => import('./pages/public/Features'));
const Pricing = lazy(() => import('./pages/public/Pricing'));
const About = lazy(() => import('./pages/public/About'));
const Contact = lazy(() => import('./pages/public/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/public/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/public/TermsOfService'));

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardHub = lazy(() => import('./pages/DashboardHub'));
const NewsletterWizard = lazy(() => import('./pages/NewsletterWizard'));
const CreateNewsletter = lazy(() => import('./pages/CreateNewsletter'));
const NewsletterPreview = lazy(() => import('./pages/NewsletterPreview'));
// #247 — NewsletterHub + CommunicationHub (singular) deleted as orphan
// routes. CommunicationsHub (plural) remains as the main hub.
const CommunicationsHub = lazy(() => import('./pages/CommunicationsHub'));
const UnifiedCampaignWizard = lazy(() => import('./pages/UnifiedCampaignWizard'));
const SeriesManagementPage = lazy(() => import('./pages/SeriesManagementPage'));
const SeriesDetailPage = lazy(() => import('./pages/SeriesDetailPage'));
const CampaignDetail = lazy(() => import('./pages/CampaignDetail'));
const ToneStyleConfiguration = lazy(() => import('./pages/ToneStyleConfiguration'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const Templates = lazy(() => import('./pages/Templates'));
const NewsletterHistory = lazy(() => import('./pages/NewsletterHistory'));
const Profile = lazy(() => import('./pages/Profile'));
const CommunicationStyles = lazy(() => import('./pages/CommunicationStyles'));
const ClientManagement = lazy(() => import('./pages/ClientManagement'));
const ClientsHub = lazy(() => import('./pages/ClientsHub'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const SystemHealthDashboard = lazy(() => import('./pages/SystemHealthDashboard'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const GlobalSearch = lazy(() => import('./pages/GlobalSearch'));
const TemplateGallery = lazy(() => import('./pages/TemplateGallery'));
const FixTemplates = lazy(() => import('./pages/FixTemplates'));
const TemplateDiagnostic = lazy(() => import('./pages/TemplateDiagnostic'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Communications components
const ReplyDetailView = lazy(() => import('./components/communications/ReplyDetailView'));
const ReplyComposer = lazy(() => import('./components/communications/ReplyComposer'));
const SettingsHub = lazy(() => import('./pages/SettingsHub'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const ContentHub = lazy(() => import('./pages/ContentHub'));
const NavigationDemo = lazy(() => import('./pages/NavigationDemo'));
const NewsletterAnalyticsPage = lazy(() => import('./pages/NewsletterAnalyticsPage'));
const PerformanceReports = lazy(() => import('./pages/PerformanceReports'));
const BatchReviewPage = lazy(() => import('./pages/BatchReviewPage'));
const ClientImport = lazy(() => import('./pages/ClientImport'));
const NotFound = lazy(() => import('./pages/NotFound'));
const EmailContentPreview = lazy(() => import('./components/communications/EmailContentPreview'));
const ClientPortalPage = lazy(() => import('./pages/ClientPortalPage'));
const ComplianceReview = lazy(() => import('./pages/ComplianceReview'));
// #315 — Drip / Sequence Campaigns MVP. Flag-gated inside the page
// component (renders null when VITE_USE_DRIP_SEQUENCES is off) so the
// route is always present but invisible to non-alpha users.
const SequenceBuilderPage = lazy(() => import('./pages/SequenceBuilderPage'));
const ScheduleManager = lazy(() => import('./components/scheduling/ScheduleManager'));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

function App() {
  return (
    <ErrorBoundary name="app-root">
    <AuthProvider>
      <LoadingProvider>
        <ToastProvider>
          <SessionTimeoutProvider>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
              {/* Public Marketing Routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
              </Route>

              {/* Client Portal — public, no auth required */}
              <Route path="/portal/:token" element={<ClientPortalPage />} />

              {/* Public Routes (Authentication) */}
              <Route path="/auth" element={<AuthLayout />}>
                <Route path="login" element={<LoginForm />} />
                <Route path="signup" element={<RegistrationForm />} />
                <Route path="verify-email" element={<EmailVerification />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="mfa-setup" element={<MFASetup />} />
                <Route path="mfa-verification" element={<MFAVerification />} />
              </Route>

              {/* Direct routes for easy access */}
              <Route path="/login" element={<LoginForm />} />
              <Route path="/signup" element={<RegistrationForm />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/mfa-setup" element={<MFASetup />} />
              <Route path="/mfa-verification" element={<MFAVerification />} />

              {/* Onboarding route — authenticated but NOT wrapped in OnboardingGuard
                  (to avoid redirect loops). Sprint 3 #72. */}
              <Route
                path="/app/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingWizard />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <Layout />
                  </OnboardingGuard>
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardHub />} />
                <Route path="dashboard-old" element={<Dashboard />} />

                {/* Communications Hub - NEW Unified Interface */}
                <Route path="communications" element={<CommunicationsHub />} />
                <Route path="communications/content/:id" element={<EmailContentPreview />} />
                <Route path="communications/inbox/:replyId" element={<ReplyDetailView />} />
                <Route path="communications/inbox/:replyId/compose" element={<ReplyComposer />} />
                <Route
                  path="communications/campaigns/create"
                  element={<Navigate to="/app/communications/campaigns/unified" replace />}
                />
                <Route path="communications/campaigns/unified" element={<UnifiedCampaignWizard />} />
                <Route path="communications/campaigns/:id" element={<CampaignDetail />} />
                {/* #395: removed the bare "Campaign Compare (Coming Soon)" placeholder route. */}
                <Route path="communications/series" element={<SeriesManagementPage />} />
                <Route path="communications/series/:id" element={<SeriesDetailPage />} />
                <Route path="communications/review" element={<ComplianceReview />} />
                <Route path="communications/sequences" element={<SequenceBuilderPage />} />

                {/* Newsletter Routes - Standardized */}
                {/* #247 — bare /app/newsletters → CommunicationsHub (the
                    unified hub in main nav). NewsletterHub component deleted. */}
                <Route path="newsletters" element={<Navigate to="/app/communications?view=campaigns" replace />} />
                <Route path="newsletters/create" element={<NewsletterWizard />} />
                <Route path="newsletters/new" element={<CreateNewsletter />} />
                <Route path="newsletters/history" element={<NewsletterHistory />} />
                <Route path="newsletters/batch/:batchId" element={<BatchReviewPage />} />
                <Route path="newsletters/builder" element={<Templates />} />
                <Route path="newsletters/analytics" element={<NewsletterAnalyticsPage />} />
                <Route path="newsletters/reports" element={<PerformanceReports />} />
                {/* Both Performance Reports drill-downs navigate here; it was unrouted. */}
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="newsletters/:id" element={<NewsletterPreview />} />

                {/* Newsletter Legacy Redirects (relative to /app) */}
                <Route path="newsletter-wizard" element={<Navigate to="/app/newsletters/create" replace />} />
                <Route path="create-newsletter" element={<Navigate to="/app/newsletters/create" replace />} />
                <Route path="newsletter-preview/:id" element={<Navigate to="/app/newsletters/:id" replace />} />
                <Route path="newsletters-history" element={<Navigate to="/app/newsletters/history" replace />} />
                <Route path="template-builder" element={<Navigate to="/app/newsletters/builder" replace />} />
                <Route path="templates" element={<TemplateGallery />} />
                <Route path="fix-templates" element={<FixTemplates />} />
                <Route path="template-diagnostic" element={<TemplateDiagnostic />} />
                <Route path="profile" element={<Navigate to="/app/settings?tab=profile" replace />} />
                {/* Communication-style editing now lives under the Profile tab
                    (the standalone AI & Content tab was removed). */}
                <Route path="communication-styles" element={<Navigate to="/app/settings?tab=profile" replace />} />
                <Route path="tone-style-config" element={<ToneStyleConfiguration />} />
                {/* #247 — singular /app/communication → CommunicationsHub
                    (plural). CommunicationHub component deleted. */}
                <Route path="communication" element={<Navigate to="/app/communications" replace />} />
                <Route path="clients" element={<ClientsHub />} />
                <Route path="clients/import" element={<ClientImport />} />
                <Route path="clients/manage" element={<ClientManagement />} />
                <Route path="clients/:clientId" element={<ClientDetail />} />
                {/* Audit 2026-09-06 §04 P0: the standalone Market Data page crashed on
                    the raw indicators shape and duplicated Content Hub. Redirect. */}
                <Route path="market-data" element={<Navigate to="/app/content" replace />} />
                <Route path="api-keys" element={<Navigate to="/app/settings?tab=security" replace />} />
                <Route path="system-health" element={<SystemHealthDashboard />} />
                <Route path="search" element={<GlobalSearch />} />
                <Route path="settings" element={<SettingsHub />} />
                <Route path="content" element={<ContentHub />} />
                <Route path="content-hub" element={<Navigate to="/app/content" replace />} />
                <Route path="schedule" element={<ScheduleManager />} />
                <Route path="branding" element={<Navigate to="/app/settings?tab=branding" replace />} />

                {/* Admin Routes */}
                <Route
                  path="admin"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />

                {/* Unknown /app/* paths stay inside the app (in-app 404) instead of
                    bouncing a signed-in advisor onto the marketing homepage. */}
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Navigation Demo - Standalone page (not in protected route layout) */}
              <Route path="/navigation-demo" element={
                <ProtectedRoute>
                  <NavigationDemo />
                </ProtectedRoute>
              } />

              {/* Catch all route - redirect to landing page */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </SessionTimeoutProvider>
        </ToastProvider>
      </LoadingProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
